import { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Volume2, VolumeX } from "lucide-react";

// Module-level registry so unmuting one video mutes all the others (exclusive sound).
const soundRegistry = new Set();

// Fallback frame shown if a video is slow or fails to load (on-brand dark/gold still).
const DEFAULT_POSTER = "/video-poster.jpg";

export function CinematicVideo({ src, testId = "cinematic-video", poster, caption, className = "", fade = false, noSound = false, sound = false, soundMask = false, silver = false, gold = true, tint = false }) {
  const { t } = useTranslation();
  const ref = useRef(null);
  const [muted, setMuted] = useState(true);
  const [visible, setVisible] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [edgeOpacity, setEdgeOpacity] = useState(0);

  // Lazy playback + fade-in: only load & play when the video approaches the viewport.
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    // Force muted attribute reliably (React's `muted` prop is not always applied,
    // which makes browsers block muted autoplay).
    v.muted = true;
    v.defaultMuted = true;
    const tryPlay = () => {
      const p = v.play?.();
      if (p && p.catch) p.catch(() => setEdgeOpacity(1)); // autoplay blocked -> at least show the frame
    };
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) { setShouldLoad(true); setVisible(true); tryPlay(); }
        else v.pause?.();
      },
      { threshold: 0.2, rootMargin: "150px 0px" }
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);

  // Fondu en boucle : la vidéo s'ouvre et se ferme en douceur à chaque boucle.
  const onTime = () => {
    const v = ref.current;
    if (!v || !v.duration || isNaN(v.duration)) return;
    const fd = 0.7;
    const cur = v.currentTime, d = v.duration;
    let o = 1;
    if (cur < fd) o = cur / fd;
    else if (cur > d - fd) o = Math.max(0, (d - cur) / fd);
    setEdgeOpacity(Number(o.toFixed(2)));
  };

  // Register a "mute me" hook for exclusive-sound coordination.
  useEffect(() => {
    const entry = { mute: () => { const v = ref.current; if (v) { v.muted = true; setMuted(true); } } };
    soundRegistry.add(entry);
    return () => soundRegistry.delete(entry);
  }, []);

  const toggleSound = () => {
    const v = ref.current;
    if (!v) return;
    const next = !v.muted;
    if (next === false) {
      // turning sound ON -> mute every video (incl. self), then unmute self below
      soundRegistry.forEach((e) => e.mute());
    }
    v.muted = next;
    if (!next) { v.volume = 1; v.play?.().catch(() => {}); }
    setMuted(next);
  };

  const content = (
    <div className={`cinematic-wrap cinematic-fadein ${visible ? "is-visible" : ""} ${className}`}>
      {!fade && <div aria-hidden className="cinematic-glow" />}
      <figure className={fade ? "relative" : "cinematic-halo cinematic-mask rounded-3xl overflow-hidden"}>
        <video
          ref={ref}
          src={shouldLoad ? src : undefined}
          poster={poster || DEFAULT_POSTER}
          loop
          muted
          playsInline
          preload={shouldLoad ? "auto" : "none"}
          onLoadedData={() => { const v = ref.current; if (v && v.paused) { v.muted = true; v.play?.().catch(() => setEdgeOpacity(1)); } }}
          onTimeUpdate={onTime}
          style={{ opacity: edgeOpacity, transition: "opacity 0.25s linear" }}
          data-testid={testId}
          className={`w-full h-auto block ${fade ? "hero-video-fade" : ""}`}
        />
        {tint && (
          <>
            <div
              aria-hidden
              className={`pointer-events-none absolute inset-0 ${fade ? "hero-video-fade" : ""}`}
              style={{
                background:
                  "radial-gradient(circle at 50% 46%, hsl(28 96% 56% / 0.50), hsl(24 95% 50% / 0.22) 40%, transparent 72%)",
                mixBlendMode: "soft-light",
                opacity: edgeOpacity,
                transition: "opacity 0.25s linear",
              }}
            />
            <div
              aria-hidden
              className={`pointer-events-none absolute inset-0 ${fade ? "hero-video-fade" : ""}`}
              style={{
                background:
                  "radial-gradient(circle at 50% 46%, hsl(30 90% 50% / 0.22), transparent 58%)",
                mixBlendMode: "overlay",
                opacity: edgeOpacity,
                transition: "opacity 0.25s linear",
              }}
            />
          </>
        )}
        {(sound || !fade) && !noSound && (
          <button
            onClick={toggleSound}
            data-testid={`${testId}-sound-btn`}
            aria-label={muted ? t("games.soundOn") : t("games.soundOff")}
            className={soundMask
              ? "absolute bottom-3 right-3 z-20 w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-background/95 backdrop-blur-md ring-1 ring-primary/25 shadow-[0_0_20px_-4px_hsl(28_96%_56%_/_0.4)] flex items-center justify-center text-foreground/80 hover:text-primary hover:ring-primary/50 transition-colors"
              : "absolute bottom-4 left-4 z-20 w-11 h-11 rounded-full bg-background/60 backdrop-blur-md border border-white/15 flex items-center justify-center text-foreground/80 hover:text-primary hover:border-primary/50 transition-colors"}
          >
            {muted ? <VolumeX size={soundMask ? 22 : 18} /> : <Volume2 size={soundMask ? 22 : 18} />}
          </button>
        )}
        {caption && <figcaption className="sr-only">{caption}</figcaption>}
      </figure>
    </div>
  );

  if (gold) {
    return (
      <div className="relative" data-testid={`${testId}-gold-frame`}>
        {/* Halo doré très doux */}
        <div aria-hidden className="pointer-events-none absolute -inset-6 -z-10">
          <div className="absolute inset-0 rounded-[3rem] blur-[80px] opacity-40"
            style={{ background: "radial-gradient(ellipse at 50% 44%, hsl(42 90% 60% / 0.22), hsl(30 85% 52% / 0.10) 48%, transparent 74%)" }} />
        </div>
        {/* Cadre doré délicat */}
        <div
          className="relative rounded-[2rem] p-[2px] sm:p-[2.5px] shine-gold"
          style={{
            backgroundImage: "linear-gradient(135deg, hsl(43 62% 62%) 0%, hsl(36 55% 50%) 30%, hsl(30 48% 42%) 52%, hsl(42 58% 58%) 76%, hsl(33 50% 46%) 100%)",
            boxShadow: "0 0 46px -20px hsl(40 80% 54% / 0.3), 0 20px 56px -34px rgba(0,0,0,0.78), inset 0 0 30px -24px hsl(46 80% 74% / 0.35)",
          }}
        >
          <div className="rounded-[1.75rem] p-1.5 sm:p-2 bg-background/85 backdrop-blur-md">
            <div className="rounded-[1.4rem] overflow-hidden">
              {content}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!silver) return content;

  return (
    <div className="relative" data-testid={`${testId}-silver-frame`}>
      {/* Halo de fraîcheur blanc argenté */}
      <div aria-hidden className="pointer-events-none absolute -inset-7 -z-10">
        <div className="absolute inset-0 rounded-[3rem] blur-[85px] opacity-75"
          style={{ background: "radial-gradient(ellipse at 50% 42%, rgba(233,240,247,0.55), rgba(196,214,230,0.24) 46%, transparent 72%)" }} />
      </div>
      {/* Cadre argenté */}
      <div
        className="relative rounded-[2rem] p-2 sm:p-2.5 border-2 border-white/45 ring-1 ring-white/20 glass"
        style={{ boxShadow: "0 0 75px -14px rgba(226,238,248,0.6), 0 26px 70px -30px rgba(0,0,0,0.8), inset 0 0 46px -26px rgba(255,255,255,0.5)" }}
      >
        <div className="rounded-[1.5rem] overflow-hidden">
          {content}
        </div>
      </div>
    </div>
  );
}
